import type { JSX } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { TableHead } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { SortState } from '@/shared/types/table-filters.types';

export interface SortableTableHeadProps {
  columnKey: string;
  label: string;
  sort: SortState | null;
  onToggleSort: (key: string) => void;
  className?: string;
}

type AriaSort = 'ascending' | 'descending' | 'none';

function ariaSortOf(sort: SortState | null, columnKey: string): AriaSort {
  if (sort?.key !== columnKey) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

function SortIcon({ ariaSort }: { ariaSort: AriaSort }): JSX.Element {
  if (ariaSort === 'ascending') return <ArrowUp className="h-3.5 w-3.5" />;
  if (ariaSort === 'descending') return <ArrowDown className="h-3.5 w-3.5" />;
  return <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />;
}

/** Table header that cycles its column through asc -> desc -> unsorted when clicked. */
export function SortableTableHead({
  columnKey,
  label,
  sort,
  onToggleSort,
  className,
}: SortableTableHeadProps): JSX.Element {
  const ariaSort = ariaSortOf(sort, columnKey);

  return (
    <TableHead className={className} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onToggleSort(columnKey)}
        className={cn(
          'flex items-center gap-1 hover:text-foreground cursor-pointer',
          ariaSort === 'none' ? 'text-muted-foreground' : 'text-foreground font-semibold'
        )}
      >
        {label}
        <SortIcon ariaSort={ariaSort} />
        {ariaSort !== 'none' && <span className="sr-only">sorted {ariaSort}</span>}
      </button>
    </TableHead>
  );
}
